import { useState } from "react";
import { AlertCircle, Ban, BadgeCheck, CheckCircle2, CircleDashed, Clock, Download, LogOut, PackageCheck, Printer, QrCode, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { money, PAY_LABEL, DELIV_LABEL, PERMIT_LABEL, exportXLSX } from "@/lib/api";

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 fade-up">
    <div>
      <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1A4D2E]" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-slate-500 mt-1 text-sm sm:text-base">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2 no-print">{children}</div>}
  </div>
);

export const Money = ({ v, className = "" }) => <span className={`num ${className}`}>{money(v)}</span>;

const Pill = ({ cls, Icon, label, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold border whitespace-nowrap ${cls}`}>
    <Icon className="w-3.5 h-3.5" aria-hidden /> {label}
  </span>
);

const PAY = { unpaid: ["bg-rose-50 text-rose-800 border-rose-300", AlertCircle], partial: ["bg-amber-50 text-amber-800 border-amber-300", Clock],
  paid: ["bg-emerald-50 text-emerald-800 border-emerald-300", CheckCircle2] };
export const PayBadge = ({ s, testid = "pay-status" }) => <Pill testid={testid} cls={PAY[s]?.[0]} Icon={PAY[s]?.[1] || Clock} label={PAY_LABEL[s]} />;

const DEL = { none: ["bg-slate-50 text-slate-700 border-slate-300", CircleDashed], ready: ["bg-sky-50 text-sky-800 border-sky-300", BadgeCheck],
  permit_issued: ["bg-violet-50 text-violet-800 border-violet-300", QrCode], delivered: ["bg-blue-50 text-blue-800 border-blue-300", PackageCheck],
  exited: ["bg-stone-200 text-stone-800 border-stone-400", LogOut], permit_cancelled: ["bg-rose-50 text-rose-800 border-rose-300", Ban] };
export const DeliveryBadge = ({ s, testid = "delivery-status" }) => <Pill testid={testid} cls={DEL[s]?.[0]} Icon={DEL[s]?.[1] || CircleDashed} label={DELIV_LABEL[s]} />;

export const PermitBadge = ({ p, testid = "permit-status" }) => {
  const s = p.status === "valid" && p.delivered_at ? "delivered" : p.status;
  const m = { valid: ["bg-violet-50 text-violet-800 border-violet-300", QrCode, PERMIT_LABEL.valid],
    delivered: ["bg-blue-50 text-blue-800 border-blue-300", PackageCheck, "صالح – سُلّم"],
    used: ["bg-stone-200 text-stone-800 border-stone-400", LogOut, "مستخدم – خرج"], cancelled: ["bg-rose-50 text-rose-800 border-rose-300", Ban, PERMIT_LABEL.cancelled] }[s];
  return <Pill testid={testid} cls={m[0]} Icon={m[1]} label={m[2]} />;
};

export const SaleStatusBadge = ({ s }) => s === "cancelled"
  ? <Pill testid="sale-status" cls="bg-rose-50 text-rose-800 border-rose-300" Icon={Ban} label="ملغى" />
  : <Pill testid="sale-status" cls="bg-emerald-50 text-emerald-800 border-emerald-300" Icon={CheckCircle2} label="مسجل" />;

export const Kpi = ({ label, value, sub, Icon, tone = "green", testid }) => {
  const tones = { green: "text-[#1A4D2E] bg-emerald-50", ochre: "text-[#B45309] bg-amber-50", blue: "text-[#1E40AF] bg-blue-50", rose: "text-rose-700 bg-rose-50", slate: "text-slate-700 bg-slate-100" };
  return (
    <div className="k-card p-5 fade-up" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-bold text-slate-500">{label}</div>
        {Icon && <span className={`rounded-xl p-2 ${tones[tone]}`}><Icon className="w-5 h-5" /></span>}
      </div>
      <div className="mt-3 text-2xl sm:text-3xl font-extrabold font-display num">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
};

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="k-label">{label}</span>
    {children}
    {hint && <span className="text-xs text-slate-500 mt-1 block">{hint}</span>}
  </label>
);

export const Empty = ({ text = "لا توجد بيانات" }) => <div className="text-center text-slate-500 py-12" data-testid="empty-state">{text}</div>;
export const Loading = () => <div className="flex justify-center py-12 text-[#1A4D2E]"><Loader2 className="w-7 h-7 animate-spin" /></div>;
export const ErrorBox = ({ text }) => text ? <div className="rounded-xl border border-rose-300 bg-rose-50 text-rose-800 p-3 text-sm font-medium" data-testid="error-box" role="alert">{text}</div> : null;

export function Modal({ open, onOpenChange, title, description, children, footer, wide }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className={`text-right max-h-[92vh] overflow-y-auto ${wide ? "sm:max-w-2xl" : "sm:max-w-lg"}`}>
        <DialogHeader className="text-right sm:text-right">
          <DialogTitle className="font-display text-xl pl-6">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        {footer && <DialogFooter className="gap-2 sm:justify-start">{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

export function ReasonDialog({ open, onOpenChange, title, description, confirmLabel = "تأكيد", danger = true, onConfirm, testid = "reason-dialog" }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async () => {
    setBusy(true); setErr("");
    try { await onConfirm(reason.trim()); setReason(""); onOpenChange(false); } catch (e) { setErr(e.message || String(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} description={description}
      footer={<>
        <button className={danger ? "k-btn-danger" : "k-btn-primary"} disabled={busy || reason.trim().length < 3} onClick={go} data-testid={`${testid}-confirm`}>{busy && <Loader2 className="w-4 h-4 animate-spin" />}{confirmLabel}</button>
        <button className="k-btn-outline" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel`}>تراجع</button>
      </>}>
      <Field label="السبب (إلزامي)"><textarea className="k-input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testid}-reason`} /></Field>
      <ErrorBox text={err} />
    </Modal>
  );
}

export const ExportBar = ({ name, cols, rows }) => (
  <>
    <button className="k-btn-outline k-btn-sm" onClick={() => exportXLSX(name, cols, rows || [])} data-testid="export-csv-btn"><Download className="w-4 h-4" /> Excel</button>
    <button className="k-btn-outline k-btn-sm" onClick={() => window.print()} data-testid="export-pdf-btn"><Printer className="w-4 h-4" /> PDF / طباعة</button>
  </>
);

export const Sel = ({ value, onChange, children, testid, className = "" }) => (
  <select className={`k-input ${className}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>{children}</select>
);
